import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, RefreshCw, Shield } from 'lucide-react';
import { Pagination } from '../../components/client';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminPlatformAudit } from '../../hooks/useAdminPlatformAudit';
import { formatDate, formatTime } from '../../utils/dateUtils';

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All event types' },
  { value: 'audit.payout.recorded', label: 'Vendor payout recorded' },
  { value: 'audit.booking.date_change.approved', label: 'Booking date change approved' },
  { value: 'audit.booking.date_change.rejected', label: 'Booking date change rejected' },
  { value: 'audit.vendor.verification.approved', label: 'Vendor verification approved' },
  { value: 'audit.vendor.verification.rejected', label: 'Vendor verification rejected' },
];

const AdminComplianceAudit = () => {
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState('');
  const { events, pagination, isLoading, error, refetch } = useAdminPlatformAudit({
    page,
    eventType,
  });

  const columns = [
    {
      key: 'created_at',
      label: 'When',
      width: 'w-40',
      render: (event) => (
        <div className="text-sm tabular-nums">
          <p className="text-gray-900">{event.created_at ? formatDate(event.created_at) : '—'}</p>
          <p className="text-gray-500 text-xs">
            {event.created_at ? formatTime(event.created_at) : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'label',
      label: 'Event',
      render: (event) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{event.label}</p>
          <p className="text-xs text-gray-500 font-mono">{event.event_type}</p>
        </div>
      ),
    },
    {
      key: 'actor',
      label: 'Actor',
      render: (event) => (
        <div className="min-w-0">
          <p className="text-sm text-gray-900 truncate">{event.actor?.name || 'System'}</p>
          <p className="text-xs text-gray-500 truncate">{event.actor?.email || event.actor_type}</p>
        </div>
      ),
    },
    {
      key: 'aggregate',
      label: 'Subject',
      render: (event) =>
        event.aggregate_id ? (
          <span className="text-sm text-gray-600">
            {event.aggregate_type} ·{' '}
            <span className="font-mono text-xs">{String(event.aggregate_id).slice(0, 8)}…</span>
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'ip_address',
      label: 'IP',
      width: 'w-28',
      render: (event) => (
        <span className="text-xs font-mono text-gray-500">{event.ip_address || '—'}</span>
      ),
    },
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6 text-center text-red-600">
          Failed to load compliance audit log.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-amber-600" />
                Compliance audit log
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Platform compliance events from the outbox <code className="text-xs bg-gray-100 px-1 rounded">audit.events</code> topic.
              </p>
              <Link
                to="/admin/analytics"
                className="mt-2 inline-block text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                ← Back to Analytics
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <SearchSelect
                value={eventType}
                onChange={(value) => {
                  setEventType(value);
                  setPage(1);
                }}
                options={EVENT_TYPE_OPTIONS}
                className="w-56"
              />
              <button
                type="button"
                onClick={() => refetch()}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading audit events…</div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileCheck className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              No compliance events found.
            </div>
          ) : (
            <>
              <DataTable
                columns={columns}
                data={events}
                searchable={false}
                filterable={false}
                showPagination={false}
                emptyMessage="No compliance events found"
              />
              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  perPage={pagination.per_page}
                  total={pagination.total}
                  onPageChange={setPage}
                  scrollToTop={false}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminComplianceAudit;
